import React, { useState, useEffect, useContext, useMemo } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { Modal, Form, Spinner, Table, Card, Row, Col, Button, Badge } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { Link, useParams } from "react-router-dom";
import {
  FaEye, FaArrowLeft, FaStore, FaMoneyBillWave, FaPlus, FaTrash, FaSave,
  FaBook, FaCalendarAlt, FaFileInvoice, FaWallet, FaCreditCard,
  FaPhone, FaEnvelope, FaMapMarkerAlt, FaBuilding, FaDownload, FaFilter, FaSearch
} from "react-icons/fa";
import Swal from 'sweetalert2';
import PurchaseDetailPanel from "./PurchaseDetailPanel";

const getTodayDate = () => new Date().toISOString().split('T')[0];

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR', minimumFractionDigits: 0, maximumFractionDigits: 0 })
    .format(amount ?? 0);

const formatDate = (dateString) => dateString
  ? new Date(dateString).toLocaleDateString('en-PK', { year: 'numeric', month: 'short', day: 'numeric' })
  : '-';

export default function VendorShow() {
  const { t } = useContext(TranslatorContext);
  const { id } = useParams();

  const [data, setData] = useState({ vendor: null, summary: {}, purchases: [], ledgerEntries: [] });
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [showSidePanel, setShowSidePanel] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [showFilter, setShowFilter] = useState(false);

  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ payments: [{ payment_method_id: "", payment_date: getTodayDate(), amount: "" }], notes: "" });
  const [paymentErrors, setPaymentErrors] = useState({});

  const AuthToken = localStorage.getItem("authToken");
  const headers = useMemo(() => ({ Authorization: `Bearer ${AuthToken}` }), [AuthToken]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [vendorRes, methodsRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/vendors/${id}/complete-ledger`, { headers }),
          axios.get(`${process.env.REACT_APP_API_URL}/payment-methods`, { headers })
        ]);

        const sortedLedger = [...(vendorRes.data.ledger || [])].sort((a, b) => {
          const dateDiff = new Date(b.date).getTime() - new Date(a.date).getTime();
          if (dateDiff !== 0) return dateDiff;
          if (a.type !== b.type) return a.type === 'purchase' ? -1 : 1;
          return b.id - a.id;
        });

        setData({
          vendor: vendorRes.data.vendor,
          summary: vendorRes.data.summary || {},
          purchases: vendorRes.data.purchases || [],
          ledgerEntries: sortedLedger
        });
        setPaymentMethods(methodsRes.data || []);
      } catch (err) {
        console.error(err);
        Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to fetch vendor data' });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, headers]);

  const handlePaymentChange = (index, field, value) => {
    setPaymentForm(prev => {
      const payments = [...prev.payments];
      payments[index][field] = value;
      return { ...prev, payments };
    });
  };

  const managePaymentRows = (action, index) => {
    if (action === 'add') {
      setPaymentForm(prev => ({ ...prev, payments: [...prev.payments, { payment_method_id: "", payment_date: getTodayDate(), amount: "" }] }));
    } else if (action === 'remove') {
      if (paymentForm.payments.length === 1) {
        return Swal.fire({ toast: true, position: 'top-end', icon: 'warning', title: t("At least one payment row is required"), showConfirmButton: false, timer: 2000 });
      }
      setPaymentForm(prev => ({ ...prev, payments: prev.payments.filter((_, i) => i !== index) }));
    }
  };

  const handleAddPaymentSubmit = async () => {
    setSubmitting(true);
    setPaymentErrors({});
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/vendors/${id}/payments`, paymentForm, { headers });
      if (res.data.status) {
        Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: t('Payment(s) added successfully!'), showConfirmButton: false, timer: 2000 });
        setShowAddPaymentModal(false);
        window.location.reload();
      }
    } catch (err) {
      if (err.response?.status === 422) setPaymentErrors(err.response.data.errors);
      Swal.fire({ toast: true, position: 'top-end', icon: 'error', title: t(err.response?.status === 422 ? 'Please check the form for errors' : 'Failed to add payment'), showConfirmButton: false, timer: 2000 });
    } finally {
      setSubmitting(false);
    }
  };

  const filteredLedger = useMemo(() => {
    return data.ledgerEntries.filter(entry => {
      const matchesSearch = !searchTerm || entry.reference_number?.toLowerCase().includes(searchTerm.toLowerCase()) || entry.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStart = !dateRange.start || entry.date >= dateRange.start;
      const matchesEnd = !dateRange.end || entry.date <= dateRange.end;
      return matchesSearch && matchesStart && matchesEnd;
    });
  }, [data.ledgerEntries, searchTerm, dateRange]);

  const exportToCSV = () => {
    const headersCSV = ['Date', 'Type', 'Reference #', 'Description', 'Debit', 'Credit', 'Balance'];
    const rows = data.ledgerEntries.map(e => [e.date, e.type, e.reference_number, e.description, e.debit, e.credit, e.balance]);
    const blob = new Blob([[headersCSV, ...rows].map(r => r.join(',')).join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    Object.assign(a, { href: url, download: `vendor_ledger_${data.vendor?.name}_${getTodayDate()}.csv` }).click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <PageLayout><div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}><Spinner animation="border" variant="primary" /><span className="ms-2">{t("loading")}...</span></div></PageLayout>;
  if (!data.vendor) return <PageLayout><div className="text-center py-5"><h4>{t("Vendor not found")}</h4><Link to="/vendor-list" className="btn btn-primary mt-3"><FaArrowLeft className="me-2" />{t('back to vendors')}</Link></div></PageLayout>;

  return (
    <PageLayout>
      <div className="container-fluid px-4 py-4">
        {/* Header */}
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
          <div>
            <h2 className="mb-1 fw-bold text-dark"><FaStore className="text-primary me-2" />{t('vendor details')}</h2>
            <p className="text-muted mb-0">{t('View vendor information and transaction history')}</p>
          </div>
          <div className="d-flex gap-2">
            <Button variant="success" onClick={exportToCSV} className="d-flex align-items-center gap-2"><FaDownload /> {t('export')}</Button>
            <Link to="/vendor-list" className="btn btn-primary d-flex align-items-center gap-2"><FaArrowLeft /> {t('back to vendors')}</Link>
          </div>
        </div>

        {/* Info Card */}
        <Card className="shadow-sm border-0 mb-4 overflow-hidden">
          <Card.Body className="p-0">
            <div className="p-4 border-bottom bg-light">
              <h3 className="fw-bold mb-1 text-dark">{data.vendor.name}</h3>
              {data.vendor.company_name && <p className="text-muted mb-0 small"><FaBuilding className="me-1" />{data.vendor.company_name}</p>}
            </div>
            <div className="p-4 bg-white">
              <Row className="g-3">
                {[
                  { icon: FaEnvelope, color: 'text-primary', label: t('email'), value: data.vendor.email ? <a href={`mailto:${data.vendor.email}`}>{data.vendor.email}</a> : '-' },
                  { icon: FaPhone, color: 'text-success', label: t('phone'), value: data.vendor.phone ? <a href={`tel:${data.vendor.phone}`}>{data.vendor.phone}</a> : '-' },
                  { icon: FaMapMarkerAlt, color: 'text-warning', label: t('address'), value: data.vendor.address || '-' }
                ].map((info, idx) => (
                  <Col md={4} key={idx}>
                    <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                      <info.icon className={info.color} size={20} />
                      <div>
                        <p className="text-muted text-uppercase small fw-bold mb-0">{info.label}</p>
                        <p className="mb-0 fw-medium text-dark text-break">{info.value}</p>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          </Card.Body>
        </Card>

        {/* Summary Cards */}
        <Row className="g-3 mb-4">
          {[
            { label: t('total purchases'), val: data.summary.total_purchases, sub: `${data.summary.total_purchases_count || 0} ${t('transactions')}`, color: 'text-primary', Icon: FaStore },
            { label: t('total paid'), val: data.summary.total_paid, sub: t('Amount paid to vendor'), color: 'text-success', Icon: FaMoneyBillWave },
            { label: t('total balance'), val: data.summary.total_balance, sub: t('Remaining payable'), color: 'text-warning', Icon: FaWallet }
          ].map((item, idx) => (
            <Col md={4} key={idx}>
              <Card className="shadow-sm border-0 h-100">
                <Card.Body className="d-flex align-items-center justify-content-between p-4">
                  <div>
                    <p className="text-muted text-uppercase small fw-bold mb-1">{item.label}</p>
                    <h3 className={`fw-extrabold mb-0 ${item.color}`}>{formatCurrency(item.val)}</h3>
                    <small className="text-muted">{item.sub}</small>
                  </div>
                  <item.Icon className={item.color} size={24} />
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Ledger Section */}
        <Card className="shadow-sm border-0">
          <Card.Body className="p-4">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
              <h5 className="fw-bold mb-0 text-dark"><FaBook className="text-primary me-2" />{t('complete ledger')}</h5>
              <div className="d-flex gap-2">
                <Button variant="secondary" size="sm" onClick={() => setShowFilter(!showFilter)} className="d-flex align-items-center gap-1"><FaFilter size={12} /> {t('filter')}</Button>
                <Button variant="primary" size="sm" onClick={() => { setPaymentForm({ payments: [{ payment_method_id: "", payment_date: getTodayDate(), amount: "" }], notes: "" }); setPaymentErrors({}); setShowAddPaymentModal(true); }} className="d-flex align-items-center gap-2"><FaPlus size={12} /> {t('add payment')}</Button>
              </div>
            </div>

            {showFilter && (
              <div className="bg-light p-3 rounded-3 mb-4 border">
                <Row className="g-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="small fw-semibold">{t('search')}</Form.Label>
                      <div className="d-flex align-items-center border rounded bg-white px-2">
                        <FaSearch className="text-muted me-2" size={14} />
                        <Form.Control type="text" className="border-0 shadow-none py-1.5" placeholder={t('Search reference or details...')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                      </div>
                    </Form.Group>
                  </Col>
                  {['start', 'end'].map((type) => (
                    <Col md={3} key={type}>
                      <Form.Group>
                        <Form.Label className="small fw-semibold">{t(`${type} date`)}</Form.Label>
                        <Form.Control type="date" value={dateRange[type]} onChange={(e) => setDateRange(p => ({ ...p, [type]: e.target.value }))} />
                      </Form.Group>
                    </Col>
                  ))}
                  <Col md={2} className="d-flex align-items-end">
                    <Button variant="secondary" size="sm" className="w-100 py-2" onClick={() => { setSearchTerm(''); setDateRange({ start: '', end: '' }); }}>{t('clear')}</Button>
                  </Col>
                </Row>
              </div>
            )}

            {filteredLedger.length > 0 ? (
              <div className="table-responsive border rounded">
                <Table hover className="align-middle mb-0">
                  <thead className="table-light text-uppercase font-monospace small">
                    <tr>
                      <th className="ps-3">#</th>
                      <th>{t('date')}</th>
                      <th>{t('type')}</th>
                      <th>{t('reference #')}</th>
                      <th className="text-end">{t('credit (payment)')}</th>
                      <th className="text-end">{t('debit (purchase)')}</th>
                      <th className="text-end pe-3">{t('balance')}</th>
                      <th className="text-center">{t('action')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...filteredLedger].reverse().map((entry, index) => (
                      <tr key={`${entry.type}-${entry.id}-${index}`}>
                        <td className="ps-3 text-muted small">{index + 1}</td>
                        <td className="text-nowrap font-monospace text-secondary"><FaCalendarAlt size={12} className="text-muted me-1.5" />{formatDate(entry.date)}</td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            {entry.type === 'purchase' ? (
                              <><FaFileInvoice className="text-primary" /><span className="fw-semibold text-dark">{t('Purchase')}</span><Badge bg={entry.purchase_type === 'regular' ? "primary-subtle" : "warning-subtle"} className={entry.purchase_type === 'regular' ? "text-primary" : "text-warning-emphasis"}>{entry.purchase_type === 'regular' ? 'Regular' : 'In-House'}</Badge></>
                            ) : (
                              <><FaCreditCard className="text-success" /><span className="fw-semibold text-dark">{t('Payment')}</span></>
                            )}
                          </div>
                        </td>
                        <td><code className="text-primary font-monospace fw-bold">{entry.reference_number || '-'}</code></td>
                        <td className="text-end text-danger fw-bold">{entry.debit > 0 ? formatCurrency(entry.debit) : '-'}</td>
                        <td className="text-end text-success fw-bold">
                          {entry.credit > 0 ? (
                            <>
                              <div>{formatCurrency(entry.credit)}</div>
                              {entry.description && <small className="text-muted fw-normal d-block" style={{ fontSize: '0.75rem' }}>{entry.description}</small>}
                            </>
                          ) : '-'}
                        </td>
                        <td className="text-end fw-bold text-dark pe-3">{formatCurrency(entry.balance)}</td>
                        <td className="text-center">
                          {entry.type === 'purchase' && (
                            <Button variant="primary" size="sm" className="d-inline-flex align-items-center gap-1 px-2.5 py-1" onClick={() => {
                              const purchase = data.purchases.find(p => p.purchase_number === entry.reference_number);
                              if (purchase) { setSelectedPurchase(purchase); setShowSidePanel(true); }
                            }}>
                              <FaEye size={12} /><span className="small">{t('view')}</span>
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="table-light border-top fw-bold">
                    <tr>
                      <td colSpan="4" className="text-end pe-3">{t('Totals')}:</td>
                      <td className="text-end text-danger">{formatCurrency(filteredLedger.filter(e => e.type === 'purchase').reduce((s, e) => s + Number(e.debit || 0), 0))}</td>
                      <td className="text-end text-success">{formatCurrency(filteredLedger.filter(e => e.type === 'payment').reduce((s, e) => s + Number(e.credit || 0), 0))}</td>
                      <td className="text-end text-primary pe-3">{formatCurrency(filteredLedger[filteredLedger.length - 1]?.balance || 0)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </Table>
              </div>
            ) : (
              <div className="text-center py-5 border rounded bg-light bg-opacity-50">
                <div className="display-4 mb-3">📊</div>
                <h6 className="text-muted mb-0">{t('No ledger entries found')}</h6>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>

      <PurchaseDetailPanel show={showSidePanel} onHide={() => { setShowSidePanel(false); setSelectedPurchase(null); }} purchase={selectedPurchase} formatCurrency={formatCurrency} formatDate={formatDate} t={t} />

      {/* Add Payment Modal */}
      <Modal show={showAddPaymentModal} onHide={() => setShowAddPaymentModal(false)} size="lg">
        <Modal.Header closeButton className="bg-light px-4 p-2">
          <Modal.Title className="fs-5 d-flex align-items-center gap-2"><FaMoneyBillWave className="text-primary" /><span className="fw-bold">{t('add payment')}</span></Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {paymentForm.payments.map((payment, index) => (
            <div key={index} className="mb-3 p-3 border rounded-3 bg-light bg-opacity-50">
              <Row className="g-3">
                <Col md={5}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold">{t('payment method')} <span className="text-danger">*</span></Form.Label>
                    <Form.Select value={payment.payment_method_id} onChange={(e) => handlePaymentChange(index, 'payment_method_id', e.target.value)}>
                      <option value="">{t('select method')}</option>
                      {paymentMethods.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </Form.Select>
                    {paymentErrors[`payments.${index}.payment_method_id`] && <div className="text-danger small mt-1">{paymentErrors[`payments.${index}.payment_method_id`]}</div>}
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold">{t('amount')} <span className="text-danger">*</span></Form.Label>
                    <Form.Control type="number" step="0.01" value={payment.amount} onChange={(e) => handlePaymentChange(index, 'amount', e.target.value)} placeholder="0.00" />
                    {paymentErrors[`payments.${index}.amount`] && <div className="text-danger small mt-1">{paymentErrors[`payments.${index}.amount`]}</div>}
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold">{t('payment date')} <span className="text-danger">*</span></Form.Label>
                    <Form.Control type="date" value={payment.payment_date} onChange={(e) => handlePaymentChange(index, 'payment_date', e.target.value)} />
                    {paymentErrors[`payments.${index}.payment_date`] && <div className="text-danger small mt-1">{paymentErrors[`payments.${index}.payment_date`]}</div>}
                  </Form.Group>
                </Col>
                <Col md={1} className="d-flex align-items-end justify-content-center">
                  {paymentForm.payments.length > 1 && <Button variant="danger" size="sm" className="mb-1 p-2" onClick={() => managePaymentRows('remove', index)}><FaTrash size={14} /></Button>}
                </Col>
              </Row>
            </div>
          ))}
          <div className="text-end mb-3"><Button variant="primary" size="sm" onClick={() => managePaymentRows('add')}><FaPlus size={10} className="me-1" />{t('add another row')}</Button></div>
          <Form.Group className="mt-3">
            <Form.Label className="small fw-semibold">{t('notes')}</Form.Label>
            <Form.Control as="textarea" rows={3} value={paymentForm.notes} onChange={(e) => setPaymentForm(p => ({ ...p, notes: e.target.value }))} placeholder={t('Optional text details for this entry...')} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="bg-light px-4 p-2">
          <Button variant="secondary" size="sm" onClick={() => setShowAddPaymentModal(false)}>{t('cancel')}</Button>
          <Button variant="primary" size="sm" onClick={handleAddPaymentSubmit} disabled={submitting}>
            {submitting ? <Spinner animation="border" size="sm" /> : <><FaSave className="me-1" />{t('save payments')}</>}
          </Button>
        </Modal.Footer>
      </Modal>
    </PageLayout>
  );
}